// components/LandingFooter.jsx
import React from 'react';
import {
  Box,
  Container,
  Grid,
  Typography,
  Divider,
  Stack,
  Link,
} from '@mui/material';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

import EmailIcon from '@mui/icons-material/EmailOutlined';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import FacebookIcon from '@mui/icons-material/Facebook';

import logo from '../assets/bouldou logo black.png';

const LandingFooter = () => {
  const navigate = useNavigate();
  const currentYear = new Date().getFullYear();

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const quickLinks = [
    { label: 'Our Mission', id: 'objective' },
    { label: 'How It Works', id: 'how-to-start' },
    { label: 'Pricing', id: 'pricing' },
    { label: 'Privacy Policy', path: '/privacy-policy' },
    { label: 'Terms of Service', path: '/terms-and-conditions' },
  ];

  const socialLinks = [
    { icon: <WhatsAppIcon />, link: 'https://whatsapp.com/channel/0029VbCR33xJENy15W0aRu13', label: 'WhatsApp' },
    { icon: <FacebookIcon />, link: 'https://www.facebook.com/bouldou.seychelles?ref=1', label: 'Facebook' },
  ];

  return (
    <Box
      component="footer"
      sx={{
        background: 'linear-gradient(135deg, #B200BB 0%, #CF003D 100%)',
        color: '#FFF',
        py: { xs: 8, md: 10 },
        mt: { xs: 12, md: 16 },
        textAlign: 'center', // Default center for mobile
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={5} justifyContent="center">
          {/* Logo & Tagline */}
          <Grid item xs={12}>
            <Box sx={{ mb: 3 }}>
              <Box
                component="img"
                src={logo}
                alt="Bouldou"
                sx={{
                  height: { xs: 40, sm: 50 },
                  filter: 'brightness(0) invert(1)',
                  mx: 'auto',
                  display: 'block',
                }}
              />
            </Box>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                mb: 1,
                fontFamily: 'Poppins, sans-serif',
              }}
            >
              Zwenn li lo Bouldou
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9, maxWidth: 360, mx: 'auto' }}>
              The only dating app made for Seychellois, by Seychellois.
            </Typography>
          </Grid>

          {/* Quick Links */}
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
              Quick Links
            </Typography>
            <Stack spacing={1.5} sx={{ alignItems: 'center' }}>
              {quickLinks.map((item) => (
                <Link
                  key={item.label}
                  component="button"
                  variant="body2"
                  onClick={() => {
                    if (item.id) scrollToSection(item.id);
                    else if (item.path) navigate(item.path);
                  }}
                  sx={{
                    color: '#FFF',
                    textDecoration: 'none',
                    fontWeight: 500,
                    opacity: 0.9,
                    '&:hover': { opacity: 1, textDecoration: 'underline' },
                  }}
                >
                  {item.label}
                </Link>
              ))}
            </Stack>
          </Grid>

          {/* Support */}
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
              Support
            </Typography>
            <Stack spacing={2} alignItems="center">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <EmailIcon sx={{ fontSize: 22 }} />
                <Link
                  href="mailto:contactbouldou@gmail.com"
                  sx={{ color: '#FFF', textDecoration: 'none' }}
                >
                  contactbouldou@gmail.com
                </Link>
              </Box>
            </Stack>
          </Grid>

          {/* Social Media */}
          <Grid item xs={12} md={3}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
              Follow Us
            </Typography>
            <Stack direction="row" spacing={3} justifyContent="center">
              {socialLinks.map((social, idx) => (
                <motion.a
                  key={idx}
                  href={social.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover={{ scale: 1.25 }}
                  whileTap={{ scale: 0.9 }}
                  style={{ display: 'inline-block' }}
                >
                  <Box
                    sx={{
                      bgcolor: 'rgba(255,255,255,0.15)',
                      color: '#FFF',
                      width: 48,
                      height: 48,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        bgcolor: 'rgba(255,255,255,0.3)',
                        transform: 'translateY(-4px)',
                      },
                    }}
                  >
                    {social.icon}
                  </Box>
                </motion.a>
              ))}
            </Stack>
          </Grid>
        </Grid>

        {/* Bottom Divider & Copyright */}
        <Divider sx={{ my: 6, bgcolor: 'rgba(255,255,255,0.2)' }} />

        <Stack
          spacing={1}
          sx={{
            textAlign: 'center',
            opacity: 0.8,
            fontSize: '0.9rem',
          }}
        >
          <Typography>
            © {currentYear} Bouldou.sc Pty Ltd. All rights reserved.
          </Typography>
          <Typography variant="caption">
            Made with love in Seychelles
          </Typography>
        </Stack>
      </Container>
    </Box>
  );
};

export default LandingFooter;